import React from "react";

// Real flags for the country rows in the sidebar, which all shared one generic
// feather `Flag` outline — eight identical amber pennants, so the icon column
// told you nothing and the eye had to read every label.
//
// ⚠️ Inline SVG, never emoji. Windows ships no flag-emoji font: 🇨🇳 renders
// there as the bare letters "CN". Same lesson as the sign-in page's corridors.
//
// Drawn for a 14px slot, which is the real constraint — a flag that is
// accurate at 60px is mush at 14. Emblems are therefore simplified rather than
// reproduced: the PRC's four small stars merge into a yellow smear, so only
// the big one is drawn; Malaysia's 14 stripes become 7. Each is still
// unmistakable at the size it is actually used, which is the job.
//
// The viewBox is 24×16 (3:2) while the CSS box is 14×14. SVG preserves the
// aspect ratio by default, so each flag renders 14 wide and 9 tall, centred in
// the same slot as every other icon — no CSS change, no shifted baseline.

// A five-pointed star as a polygon, so stars can be placed by centre and
// radius instead of by hand-tuned path data at four different sizes.
const star = (cx, cy, r, rotation = -90) => {
  const points = [];
  for (let i = 0; i < 10; i += 1) {
    const radius = i % 2 === 0 ? r : r * 0.382; // golden ratio: a real star
    const angle = ((rotation + i * 36) * Math.PI) / 180;
    points.push(
      `${(cx + radius * Math.cos(angle)).toFixed(2)},${(
        cy +
        radius * Math.sin(angle)
      ).toFixed(2)}`,
    );
  }
  return points.join(" ");
};

// Every flag sits in the same frame: rounded corners, and a hairline over the
// top so the white in India, Singapore, Thailand, Malaysia and Pakistan does
// not dissolve into the white sidebar.
const Flag = ({ className, title, children }) => (
  <svg
    viewBox="0 0 24 16"
    className={className}
    role="img"
    aria-label={title}
    focusable="false"
  >
    <defs>
      <clipPath id={`ars-flag-${title.replace(/\s+/g, "-").toLowerCase()}`}>
        <rect x="0" y="0" width="24" height="16" rx="2" />
      </clipPath>
    </defs>
    <g
      clipPath={`url(#ars-flag-${title
        .replace(/\s+/g, "-")
        .toLowerCase()})`}
    >
      {children}
    </g>
    <rect
      x="0.5"
      y="0.5"
      width="23"
      height="15"
      rx="1.75"
      fill="none"
      stroke="rgba(6, 33, 23, 0.28)"
    />
  </svg>
);

export const ChinaFlag = (props) => (
  <Flag {...props} title="China">
    <rect width="24" height="16" fill="#ee1c25" />
    {/* The big star sits in the canton, NOT centred — a red field with one
        centred yellow star is Vietnam's flag, which is what the first attempt
        drew. The four small ones are dots rather than stars: at 14px a
        0.6px star is a smear, but a speck still reads as the cluster that
        makes this China and not Vietnam. */}
    <polygon points={star(5.4, 4.6, 3.4)} fill="#ffde00" />
    <circle cx="9.9" cy="1.9" r="0.62" fill="#ffde00" />
    <circle cx="11.6" cy="3.7" r="0.62" fill="#ffde00" />
    <circle cx="11.6" cy="6.2" r="0.62" fill="#ffde00" />
    <circle cx="9.9" cy="8" r="0.62" fill="#ffde00" />
  </Flag>
);

export const IndiaFlag = (props) => (
  <Flag {...props} title="India">
    <rect width="24" height="16" fill="#ff9933" />
    <rect y="5.33" width="24" height="5.34" fill="#fff" />
    <rect y="10.67" width="24" height="5.33" fill="#138808" />
    <circle cx="12" cy="8" r="2.1" fill="none" stroke="#000080" strokeWidth="1.1" />
    <circle cx="12" cy="8" r="0.55" fill="#000080" />
  </Flag>
);

export const ThailandFlag = (props) => (
  <Flag {...props} title="Thailand">
    <rect width="24" height="16" fill="#fff" />
    <rect width="24" height="2.7" fill="#a51931" />
    <rect y="13.3" width="24" height="2.7" fill="#a51931" />
    <rect y="5.3" width="24" height="5.4" fill="#2d2a4a" />
  </Flag>
);

export const SingaporeFlag = (props) => (
  <Flag {...props} title="Singapore">
    <rect width="24" height="16" fill="#fff" />
    <rect width="24" height="8" fill="#ef3340" />
    {/* Crescent: a white disc with the field colour bitten out of it. */}
    <circle cx="6" cy="4" r="3" fill="#fff" />
    <circle cx="7.6" cy="4" r="2.6" fill="#ef3340" />
    {/* Five stars in a ring, as on the flag — not the three the first pass
        drew, which read as "some stars". */}
    {[-90, -18, 54, 126, 198].map((angle) => {
      const radians = (angle * Math.PI) / 180;
      return (
        <polygon
          key={angle}
          points={star(
            11.9 + 2.15 * Math.cos(radians),
            4.25 + 2.15 * Math.sin(radians),
            0.95,
          )}
          fill="#fff"
        />
      );
    })}
  </Flag>
);

// "Dubai" in the menu is the United Arab Emirates.
export const UaeFlag = (props) => (
  <Flag {...props} title="Dubai">
    <rect width="24" height="16" fill="#fff" />
    <rect width="24" height="5.33" fill="#00732f" />
    <rect y="10.67" width="24" height="5.33" fill="#000" />
    <rect width="6" height="16" fill="#ff0000" />
  </Flag>
);

export const HongKongFlag = (props) => (
  <Flag {...props} title="Hongkong">
    <rect width="24" height="16" fill="#de2910" />
    {/* The bauhinia, as five petals — the detail inside them is invisible
        here, the rosette silhouette is what reads. */}
    {[0, 72, 144, 216, 288].map((angle) => (
      <ellipse
        key={angle}
        cx="12"
        cy="4.4"
        rx="1.5"
        ry="3.1"
        fill="#fff"
        transform={`rotate(${angle} 12 8)`}
      />
    ))}
  </Flag>
);

export const MalaysiaFlag = (props) => (
  <Flag {...props} title="Malaysia">
    <rect width="24" height="16" fill="#fff" />
    {/* Seven stripes rather than fourteen — at this size fourteen is grey. */}
    {[0, 2, 4, 6].map((i) => (
      <rect key={i} y={i * 2.29} width="24" height="2.29" fill="#cc0001" />
    ))}
    <rect width="12" height="9.14" fill="#010066" />
    <circle cx="5.2" cy="4.6" r="2.6" fill="#ffcc00" />
    <circle cx="6.5" cy="4.6" r="2.2" fill="#010066" />
    <polygon points={star(9.1, 4.6, 1.9)} fill="#ffcc00" />
  </Flag>
);

export const PakistanFlag = (props) => (
  <Flag {...props} title="Pakistan">
    <rect width="24" height="16" fill="#01411c" />
    <rect width="6" height="16" fill="#fff" />
    <circle cx="14" cy="8" r="4" fill="#fff" />
    <circle cx="15.6" cy="7.2" r="3.4" fill="#01411c" />
    <polygon points={star(17.6, 5.2, 1.9)} fill="#fff" />
  </Flag>
);

// Keyed by the menu titles in constants/menu.js, which are lowercase.
export const COUNTRY_FLAGS = {
  china: ChinaFlag,
  india: IndiaFlag,
  thailand: ThailandFlag,
  singapore: SingaporeFlag,
  dubai: UaeFlag,
  hongkong: HongKongFlag,
  malaysia: MalaysiaFlag,
  pakistan: PakistanFlag,
};
